'use client';

import type { FC, ReactNode } from 'react';
import { useWikiDrops, type WikiDropsTarget } from '../../../queries/useWikiDropsQuery';
import { cn } from '../../../utils/cn';
import { wikiUiText } from '../wikiText';
import type { WikiPageText } from '../wiki.types';
import { WikiArticleAside, WikiFaq } from './WikiEditorial';
import { WikiRecentDropsBlock } from './WikiRecentDropsBlock';
import { WikiStickySide } from './WikiStickySide';
import type { Language } from '../../../i18n/types';

interface WikiArticleWithDropsProps {
  lang: Language;
  target: WikiDropsTarget;
  article: ReactNode;
  faq: WikiPageText['faq'];
}

// WHY: статью рисует сервер, клиент только раскладывает сетку. Колонка под выпадения есть, пока они грузятся или если нашлись;
// иначе раскладка как у WikiArticleAndFaq — статья без вопросов во всю ширину, у страницы без текста нет пустой ячейки
export const WikiArticleWithDrops: FC<WikiArticleWithDropsProps> = ({ lang, target, article, faq }) => {
  const t = wikiUiText(lang);
  const { data, isPending, isError } = useWikiDrops(lang, target);
  const hasFaq = faq.length > 0;

  if (isError || (!isPending && data.length === 0)) {
    if (!article) return hasFaq ? <WikiFaq items={faq} t={t} className="lg:col-start-1 lg:max-w-none" /> : null;
    return (
      <>
        <div className={hasFaq ? 'lg:col-start-1' : 'lg:col-span-2 lg:max-w-[760px]'}>{article}</div>
        {hasFaq && <WikiArticleAside faq={faq} t={t} className="lg:col-start-2" />}
      </>
    );
  }

  return (
    <>
      {article ? (
        <div className="lg:col-start-1">{article}</div>
      ) : (
        hasFaq && <WikiFaq items={faq} t={t} className="lg:col-start-1 lg:max-w-none" />
      )}
      <WikiStickySide className={cn('contents lg:flex lg:flex-col lg:gap-8', article || hasFaq ? 'lg:col-start-2' : 'lg:col-start-1')}>
        {article && hasFaq && <WikiFaq items={faq} t={t} className="max-w-none" />}
        <WikiRecentDropsBlock lang={lang} target={target} />
      </WikiStickySide>
    </>
  );
};
